import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, Phone, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/landing/Reveal";
import { SERVICE_PAGES, SERVICE_AREAS, COMPANY } from "@/data";

export default function ServicePage() {
  const { slug } = useParams();
  const page = SERVICE_PAGES.find((s) => s.slug === slug);
  if (!page) return <Navigate to="/404" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: page.serviceType,
    name: page.schemaName,
    provider: { "@id": "https://almeidashomeservices.com/#business" },
    areaServed: SERVICE_AREAS.map((a) => `${a.city}, ${a.state}`),
    url: `https://almeidashomeservices.com/services/${page.slug}`,
  };

  return (
    <>
      <Seo
        title={page.title}
        description={page.meta}
        path={`/services/${page.slug}`}
        image={page.image.replace("w=1600", "w=1200&h=630&fit=crop")}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-navy-dark pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-testid="service-hero">
        <img src={page.image} alt={page.alt} className="absolute inset-0 w-full h-full object-cover opacity-30" width="1600" height="900" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/70 to-navy-dark/40" />
        <div className="relative max-w-[1100px] mx-auto px-5 md:px-12">
          <Reveal>
            <Link to="/" className="font-body text-gold/80 hover:text-gold text-sm">← Home</Link>
            <span className="block mt-5 font-display uppercase tracking-[0.2em] text-gold text-sm font-semibold">Our Services</span>
            <h1 className="mt-3 font-display font-bold uppercase text-white text-4xl md:text-6xl leading-[0.92] tracking-tight max-w-[18ch]">
              {page.h1}
            </h1>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold hover:bg-gold-hover text-navy-dark font-bold uppercase tracking-wide text-sm px-8 py-4 transition-colors duration-200">
                Get Free Estimate <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/40 text-white hover:border-gold hover:text-gold font-semibold text-sm px-8 py-4 transition-colors duration-200">
                <Phone size={18} /> Call {COMPANY.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-5 md:px-12 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="font-body text-navy/75 text-lg md:text-xl leading-relaxed">{page.body}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-12 font-display font-bold uppercase text-navy text-2xl md:text-3xl tracking-tight">What we cover</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {page.h2s.map((h) => (
                  <div key={h} className="flex items-center gap-3 bg-cloud rounded-xl px-5 py-4">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                      <Check size={16} className="text-navy-dark" strokeWidth={3} />
                    </span>
                    <span className="font-body font-semibold text-navy">{h}</span>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Placeholder: recent projects */}
            <Reveal delay={0.15}>
              <h2 className="mt-12 font-display font-bold uppercase text-navy text-2xl md:text-3xl tracking-tight">Recent projects</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="service-photo-placeholder">
                {[0, 1].map((n) => (
                  <div key={n} className="aspect-[4/3] rounded-2xl border-2 border-dashed border-navy/15 bg-cloud flex items-center justify-center text-center p-6">
                    <span className="font-body text-navy/40 text-sm">[ Add a real project photo here ]</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Sidebar CTA + city links */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              <div className="bg-navy rounded-2xl p-7 text-white">
                <h3 className="font-display font-bold text-2xl">Free, no-obligation estimate</h3>
                <p className="mt-2 font-body text-white/70 text-sm">Tell us about your project and we'll send a clear written quote.</p>
                <Link to="/contact" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold hover:bg-gold-hover text-navy-dark font-bold uppercase tracking-wide text-sm px-6 py-3.5 transition-colors">
                  Request Estimate
                </Link>
              </div>
              <div className="bg-cloud rounded-2xl p-7">
                <h3 className="font-display font-semibold text-navy text-lg flex items-center gap-2">
                  <MapPin size={18} className="text-royal" /> Areas we serve
                </h3>
                <ul className="mt-4 space-y-2">
                  {SERVICE_AREAS.map((a) => (
                    <li key={a.slug}>
                      <Link to={`/service-areas/${a.slug}`} className="font-body text-navy/75 hover:text-royal transition-colors text-[15px]">
                        {a.city}, {a.state}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to="/service-areas" className="mt-4 inline-block font-body text-royal font-semibold text-sm hover:underline">All service areas →</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Other services */}
      <section className="bg-cloud py-16 md:py-20">
        <div className="max-w-[1100px] mx-auto px-5 md:px-12">
          <h2 className="font-display font-bold uppercase text-navy text-2xl md:text-3xl tracking-tight">Explore more services</h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {SERVICE_PAGES.filter((s) => s.slug !== page.slug).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="rounded-full bg-white border border-navy/10 px-5 py-2.5 font-body text-sm font-semibold text-navy/80 hover:border-gold hover:text-navy transition-colors">
                {s.nav}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
